/** The selection's summary at the top of the Selection tab (BAR's
 *  selection panel): how many, health and ammo, trouble, and a chip per
 *  type. A store the game writes a few times a second. */

import { For, Show, createSignal, type JSX } from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import { render } from "solid-js/web";

export interface Summary {
  count: number;
  health: string;
  ammo: string;
  /** "3 out of ammo · 1 knocked out", or blank. */
  trouble: string;
  types: string[];
  /** Selected engineers, each with what it's doing ("Building Depot
   *  45%"): a row each, clicked to centre on it. */
  jobs?: { id: number; text: string; warn: boolean }[];
  /** What the selection's on its way to do, if worth a line ("Waiting for
   *  a train at 412, 380"). */
  doing?: string;
  /** The policy on its formation, and which rule acted last. */
  policy?: string;
  /** Several structures: lines of what they make and hold together, and
   *  which (for "Decommission all"). */
  group?: { lines: string[]; ids: number[]; sites?: [number, number][] };
}

const [summary, setStore] = createStore<Summary>({ count: 0, health: "", ammo: "", trouble: "", types: [], jobs: [] });

export function setSummary(next: Summary | null): void {
  setStore(reconcile(next ?? { count: 0, health: "", ammo: "", trouble: "", types: [], jobs: [] }, { key: null, merge: true }));
}

/** "Decommission all" for a group of structures (the game sets it); armed
 *  by a first click, sent by a second. */
let onDecommission: (ids: number[]) => void = () => {};
export function setGroupDecommission(f: (ids: number[]) => void): void {
  onDecommission = f;
}
const [armed, setArmed] = createSignal(false);
/** "Cancel all" for selected construction sites (the game sets it). */
let onCancelSites: (at: [number, number][]) => void = () => {};
export function setGroupCancel(f: (at: [number, number][]) => void): void {
  onCancelSites = f;
}

/** What clicking an engineer's row does (the game sets it). */
let onJob: (id: number) => void = () => {};
export function setJobClick(f: (id: number) => void): void {
  onJob = f;
}

function SelectionSummary(): JSX.Element {
  return (
    <Show when={summary.count > 0}>
      <div class="selection-info" aria-live="polite">
        <div>
          <b>{summary.count} selected</b>
          <Show when={summary.health}> · health {summary.health}</Show>
          {/* Unarmed (engineers, prospectors): no ammo to speak of. */}
          <Show when={summary.ammo !== "–"}> · ammo {summary.ammo}</Show>
          <Show when={summary.trouble}>
            {" · "}
            <span class="warn">{summary.trouble}</span>
          </Show>
        </div>
        <div class="types">
          <For each={summary.types}>{(t) => <span>{t}</span>}</For>
        </div>
        <Show when={summary.group}>
          {(g) => (
            <div class="group-info">
              <For each={g().lines}>{(l) => <div class="muted">{l}</div>}</For>
              <Show when={g().sites}>
                {(at) => (
                  <button data-tip="All the steel paid into them goes back home" onClick={() => onCancelSites(at())}>
                    Cancel all
                  </button>
                )}
              </Show>
              <Show when={!g().sites}>
              <button
                class="decommission"
                classList={{ armed: armed() }}
                onClick={() => {
                  if (armed()) {
                    setArmed(false);
                    onDecommission(g().ids);
                  } else {
                    setArmed(true);
                    setTimeout(() => setArmed(false), 4000);
                  }
                }}
              >
                {armed() ? `Confirm: decommission ${g().ids.length}` : "Decommission all"}
              </button>
              </Show>
            </div>
          )}
        </Show>
        <Show when={summary.doing}>
          <div class="muted">{summary.doing}</div>
        </Show>
        <Show when={summary.policy}>
          <div class="muted policy-status">{summary.policy}</div>
        </Show>
        <Show when={summary.jobs?.length}>
          <div class="jobs">
            <For each={summary.jobs}>
              {(j) => (
                <button class="job" classList={{ warn: j.warn }} onClick={() => onJob(j.id)}>
                  {j.text}
                </button>
              )}
            </For>
          </div>
        </Show>
      </div>
    </Show>
  );
}

render(() => <SelectionSummary />, document.getElementById("selection-summary")!);
